/**
 * client/input/dualsense-hid.ts — optional WebHID rumble for the DualSense (Chrome/Edge desktop,
 * mainly Windows where the Gamepad API has no DualSense haptics) [CTL §8.3].
 *
 * OWNER: INPUT agent.
 * Output reports from Linux drivers/hid/hid-playstation.c (struct dualsense_output_report_common, 47 bytes,
 * static_assert'ed in the driver): byte 0 valid_flag0, 1 valid_flag1, 2 motor_right (weak), 3 motor_left
 * (strong), ..., 38 valid_flag2, 41 lightbar_setup, 42 led_brightness, 43 player_leds, 44..46 RGB.
 * (research/controller.md §8.3 says "offset 39" for valid_flag2; the driver struct puts it at 38.)
 *  - USB: report 0x02 = common block (+ reserved).
 *  - Bluetooth: report 0x31 = [seq_tag (seq<<4), tag 0x10, common(47), reserved(24), crc32 LE], CRC32 over
 *    0xA2, 0x31 and the payload without the CRC.
 * Rumble: valid_flag0 |= COMPATIBLE_VIBRATION (0x01) | HAPTICS_SELECT (0x02). Lightbar/adaptive triggers are
 * left untouched (BO2 on PS3 had neither; adaptive triggers stay off for accuracy).
 * Permission UX: requestDevice only from a user gesture (settings button); later visits use getDevices().
 */

export const DS_VENDOR = 0x054c;
export const DS_PRODUCTS = [0x0ce6, 0x0df2] as const; // DualSense, DualSense Edge
const DS_COMMON_SIZE = 47;
const DS_USB_REPORT_ID = 0x02;
const DS_BT_REPORT_ID = 0x31;
const DS_BT_PAYLOAD = 77; // 78-byte report minus the report ID byte
const VALID_FLAG0_COMPATIBLE_VIBRATION = 0x01;
const VALID_FLAG0_HAPTICS_SELECT = 0x02;

let crcTable: Uint32Array | null = null;
export function crc32(bytes: Uint8Array, seed = 0xffffffff): number {
  if (!crcTable) {
    crcTable = new Uint32Array(256);
    for (let n = 0; n < 256; n++) {
      let c = n;
      for (let k = 0; k < 8; k++) c = c & 1 ? 0xedb88320 ^ (c >>> 1) : c >>> 1;
      crcTable[n] = c >>> 0;
    }
  }
  let c = seed;
  for (let i = 0; i < bytes.length; i++) c = crcTable[(c ^ bytes[i]) & 0xff] ^ (c >>> 8);
  return c >>> 0;
}

function motor(v: number): number {
  return Math.max(0, Math.min(255, Math.round(v * 255)));
}

function writeCommon(buf: Uint8Array, off: number, strong: number, weak: number): void {
  buf[off + 0] = VALID_FLAG0_COMPATIBLE_VIBRATION | VALID_FLAG0_HAPTICS_SELECT;
  buf[off + 1] = 0;
  buf[off + 2] = motor(weak); //   motor_right (small / high frequency)
  buf[off + 3] = motor(strong); // motor_left (large / low frequency)
}

/** USB output report 0x02 data (report ID excluded, as WebHID sendReport wants it). */
export function buildUsbRumbleReport(strong: number, weak: number): Uint8Array {
  const buf = new Uint8Array(DS_COMMON_SIZE);
  writeCommon(buf, 0, strong, weak);
  return buf;
}

/** Bluetooth output report 0x31 data (report ID excluded), with sequence tag and CRC32. */
export function buildBtRumbleReport(seq: number, strong: number, weak: number): Uint8Array {
  const buf = new Uint8Array(DS_BT_PAYLOAD);
  buf[0] = (seq & 0x0f) << 4; // seq_tag
  buf[1] = 0x10; //              tag
  writeCommon(buf, 2, strong, weak);
  const crcInput = new Uint8Array(2 + DS_BT_PAYLOAD - 4);
  crcInput[0] = 0xa2; crcInput[1] = DS_BT_REPORT_ID;
  crcInput.set(buf.subarray(0, DS_BT_PAYLOAD - 4), 2);
  const crc = crc32(crcInput) ^ 0xffffffff;
  const c = crc >>> 0;
  buf[DS_BT_PAYLOAD - 4] = c & 0xff;
  buf[DS_BT_PAYLOAD - 3] = (c >>> 8) & 0xff;
  buf[DS_BT_PAYLOAD - 2] = (c >>> 16) & 0xff;
  buf[DS_BT_PAYLOAD - 1] = (c >>> 24) & 0xff;
  return buf;
}

// ---- DOM driver (only touches navigator.hid when the user opts in) ---------------------------
interface HidReportInfo { reportId: number }
interface HidCollectionInfo { outputReports?: HidReportInfo[]; inputReports?: HidReportInfo[] }
interface HidDeviceLike {
  opened: boolean;
  vendorId: number;
  productId: number;
  productName: string;
  collections: HidCollectionInfo[];
  open(): Promise<void>;
  close(): Promise<void>;
  sendReport(reportId: number, data: BufferSource): Promise<void>;
}
interface HidLike {
  requestDevice(o: { filters: { vendorId: number; productId: number }[] }): Promise<HidDeviceLike[]>;
  getDevices(): Promise<HidDeviceLike[]>;
}

function hid(): HidLike | null {
  const n = (globalThis as { navigator?: { hid?: HidLike } }).navigator;
  return n?.hid ?? null;
}

export function webHidAvailable(): boolean {
  return hid() !== null;
}

export class DualSenseHid {
  private dev: HidDeviceLike | null = null;
  private bt = false;
  private seq = 0;
  private busy = false;
  private lastStrong = -1;
  private lastWeak = -1;
  failed = false;

  get connected(): boolean { return !!this.dev?.opened; }
  get name(): string { return this.dev?.productName ?? ''; }

  /** Must run inside a click/keypress handler (Chrome shows the device chooser). */
  async request(): Promise<boolean> {
    const h = hid();
    if (!h) return false;
    try {
      const devs = await h.requestDevice({ filters: DS_PRODUCTS.map((p) => ({ vendorId: DS_VENDOR, productId: p })) });
      return await this.use(devs[0] ?? null);
    } catch {
      return false;
    }
  }

  /** Reuse a previously granted device without prompting. */
  async reconnect(): Promise<boolean> {
    const h = hid();
    if (!h) return false;
    try {
      const devs = await h.getDevices();
      return await this.use(devs.find((d) => d.vendorId === DS_VENDOR && (DS_PRODUCTS as readonly number[]).includes(d.productId)) ?? null);
    } catch {
      return false;
    }
  }

  private async use(d: HidDeviceLike | null): Promise<boolean> {
    if (!d) return false;
    if (!d.opened) await d.open();
    this.dev = d;
    // Bluetooth exposes the 0x31 output report; USB exposes 0x02.
    this.bt = d.collections.some((c) => (c.outputReports ?? []).some((r) => r.reportId === DS_BT_REPORT_ID))
      && !d.collections.some((c) => (c.outputReports ?? []).some((r) => r.reportId === DS_USB_REPORT_ID));
    this.failed = false;
    this.lastStrong = this.lastWeak = -1;
    return true;
  }

  /** Send motor levels (skips duplicates; one report in flight at a time). */
  send(strong: number, weak: number): void {
    const d = this.dev;
    if (!d || !d.opened || this.busy || this.failed) return;
    const s = motor(strong), w = motor(weak);
    if (s === this.lastStrong && w === this.lastWeak) return;
    this.lastStrong = s; this.lastWeak = w;
    const data = this.bt ? buildBtRumbleReport(this.seq++ & 0x0f, strong, weak) : buildUsbRumbleReport(strong, weak);
    this.busy = true;
    d.sendReport(this.bt ? DS_BT_REPORT_ID : DS_USB_REPORT_ID, data as unknown as BufferSource)
      .catch(() => { this.failed = true; })
      .finally(() => { this.busy = false; });
  }

  /** "Reset": stop motors and stop writing. */
  async close(): Promise<void> {
    const d = this.dev;
    this.dev = null;
    if (!d) return;
    try {
      if (d.opened) {
        await d.sendReport(this.bt ? DS_BT_REPORT_ID : DS_USB_REPORT_ID, (this.bt ? buildBtRumbleReport(this.seq++ & 0x0f, 0, 0) : buildUsbRumbleReport(0, 0)) as unknown as BufferSource);
        await d.close();
      }
    } catch { /* device gone */ }
  }
}
